import { autoinject } from "aurelia-dependency-injection";
import { ValidationController, ValidationRules } from "aurelia-validation";
import * as moment from "moment";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { PTDateTimeHelper } from "../../helpers/pt-datetime-helper";
import { NewPortCallTypeModel } from "../../models/create-vessel-port-arrival-model";
import { PortsAndTerminalsService } from "../../services/ports-and-terminals.service";

@autoinject
export class UpdateVesselArrivalDepartureDialog {
    public vesselName: string;
    public vesselImoNumber: string;
    onSave: () => void;
    public updateEtd: boolean = false;
    private _portOffset: string;

    model: NewPortCallTypeModel = {
        portCallId: null,
        portSKey: null,
        berthSKey: null,
        terminalSKey: null,
        arrivalDate: null,
        departureDate: null,
        arrivalTime: null,
        departureTime: null,
        vesselSKey: null
    }

    constructor(private dialogClientService: DialogClientService,
        private portsAndTerminalsService: PortsAndTerminalsService,
        private validationController: ValidationController,
        private notificationClientService: NotificationClientService) {
        this.validationController.reset();
        this.validationController.addRenderer(new CustomValidationRenderer());
    }

    async activate(model: { item, updateEtd: boolean, onSave: () => void }) {
        this.vesselName = model?.item?.vesselName;
        this.vesselImoNumber = `${model.item.vesselIdentifierType} ${model.item.vesselIdentifier}`;
        this.updateEtd = model?.updateEtd;

        this.model.portCallId = model?.item?.id;
        this.model.vesselSKey = model?.item?.vesselSKey;
        this.model.portSKey = model?.item?.portSKey;
        this.model.terminalSKey = model?.item?.terminalSKey;
        this.model.berthSKey = model?.item?.berthSKey;
        this.model.departureDate = PTDateTimeHelper.getDateWithoutTimezoneInfo(model.item?.etd);
        this.model.arrivalDate = PTDateTimeHelper.getDateWithoutTimezoneInfo(model.item?.eta);
        this.model.departureTime = PTDateTimeHelper.getDateWithoutTimezoneInfo(model.item?.etd);
        this.model.arrivalTime = PTDateTimeHelper.getDateWithoutTimezoneInfo(model.item?.eta);

        this.onSave = model.onSave;

        this._portOffset = await this.portsAndTerminalsService.getPortOffset(this.model.portSKey);
    }

    close() {
        this.dialogClientService.hide();
    }

    submit() {
        return new Promise<void>((resolve, reject) => {
            this.initValidationRules();
            this.validationController.validate().then(response => {
                if (!response.valid) {
                    reject();
                    return;
                }
                
                //cloning before update so that time/date in ui would not show different than value set at any point, since we are change to utc using port timezone instead of user timezone
                const updateModel = CloneHelper.cloneObject(this.model);
                updateModel.arrivalDate = PTDateTimeHelper.convertToUtc(this.model.arrivalDate, this.model.arrivalTime, this._portOffset);
                updateModel.departureDate = PTDateTimeHelper.convertToUtc(this.model.departureDate, this.model.departureTime, this._portOffset);

                this.portsAndTerminalsService.updateVesselPortArrival({ vesselPortCalls: [updateModel] })
                    .then(() => {
                        this.notificationClientService.show(``, `Vessel port arrival updated successfully`, NotificationType.Success);
                        this.close();
                        if (this.onSave) {
                            this.onSave();
                        }
                        resolve();
                    }).catch(() => {
                        this.notificationClientService.show(``, `Failed to update vessel port arrival`, NotificationType.Error);
                        reject();
                    });
            });
        })
    }

    initValidationRules() {
        ValidationRules
            .ensure<NewPortCallTypeModel, string | Date>(vpa => vpa.arrivalDate)
            .displayName('Arrival date')
            .required()
            .satisfies(x => moment(x).isSameOrAfter(moment(), 'day'))
            .withMessage("Arrival date must be greater than today's date")

            .ensure<string | Date>(vpa => vpa.arrivalTime)
            .displayName('Arrival time')
            .required()
            .satisfies(x => {
                const date = moment(x);
                const today = moment();

                return date.hour() > today.hour()
                    || (date.hour() == today.hour() && date.minutes() >= today.minutes());
            })
            .when(x => moment(x.arrivalDate).isSame(moment(), 'day'))
            .withMessage("Arrival time must be greater than current time")
            .on(this.model);
    }

}